import React from 'react';

interface ScenarioProps {
  title: string;
  question: string;
  clientName: string;
  children: React.ReactNode;
  voyantNote?: string;
  voyantReadbacks?: React.ReactNode;
  isCommitted: boolean;
  onCommit?: () => void;
  onSkip?: () => void;
  hideCommitButtons?: boolean;
}

const ScenarioScreenWrapper: React.FC<ScenarioProps> = ({
  title,
  question,
  clientName,
  children,
  voyantNote,
  voyantReadbacks,
  isCommitted,
  onCommit,
  onSkip,
  hideCommitButtons = false,
}) => {
  return (
    <div className="max-w-3xl mx-auto py-8 px-6">
      <h2 className="text-xl font-bold text-ma-navy mb-2">{title}</h2>
      <p className="text-lg italic text-gray-600 mb-8 leading-relaxed">"{question}"</p>

      {/* Client Aspiration */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Client Aspiration
        </div>
        {children}
      </div>

      {/* Voyant Break */}
      {voyantNote && (
        <div className="mb-8">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
            Voyant Break
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">{voyantNote}</p>
          </div>
          {isCommitted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-green-600 font-bold">✓</span>
                <span className="text-sm font-medium text-green-800">Committed — {clientName}</span>
              </div>
              {voyantReadbacks}
            </div>
          ) : (
            voyantReadbacks
          )}
        </div>
      )}

      {/* Commit / Skip */}
      {!hideCommitButtons && !isCommitted && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              Skip this scenario
            </button>
          )}
          {onCommit && (
            <button
              type="button"
              onClick={onCommit}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition"
            >
              ✓ Commit
            </button>
          )}
        </div>
      )}

      {isCommitted && !hideCommitButtons && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">✓</div>
          <div className="text-sm font-medium text-green-800">Scenario committed to the plan</div>
        </div>
      )}
    </div>
  );
};

export default ScenarioScreenWrapper;
